import { FC, ReactElement, useEffect, useRef, useState } from "react";
import { GptStatus, IGptConfiguration } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { Badge, Body2, Caption1, Card, CardFooter, CardHeader, Image, InfoLabel, makeStyles, shorthands, Subtitle1, Subtitle2, tokens } from "@fluentui/react-components";
import { useRootState, useUserPreferences } from "../../store/hooks";
import { getGptIconAsset } from "../../Utils/image";
import { performEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import moment from "moment";
import { editGptConfigurations, setGptConfigurationsAction } from "../../store/actions/gptConfigurationsAction";


const useStyles = makeStyles({
    gptsContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        ...shorthands.gap('20px'),
        ...shorthands.margin('20px', '0'),
        '@media (max-width: 768px)': {
            ...shorthands.gap('16px'),
            ...shorthands.margin('16px', '0'),
        },
    },
    card: {
        width: '400px',
        minHeight: '270px',
        boxShadow: tokens.shadow16,
        '@media (max-width: 768px)': {
            width: '100%',
            minWidth: '280px',
            maxWidth: '400px',
        },
    },
    cardBody: {
        height: '100%',
    },
    headerImage: {
        ...shorthands.borderRadius('4px'),
        height: "50px",
    },
    staticCardContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '20px',
        ...shorthands.gap('1rem'),
        height: '100%',
        '> svg': {
            color: tokens.colorBrandBackground,
            width: '60px',
            height: '60px'
        }
    },
    staticCardFooterContainer: {
        display: 'flex',
        flexDirection: 'row-reverse'
    }
});

export interface ExploreGptSectionProps {
    title: string;
    infoLabel?: ReactElement;
    gpts: IGptConfiguration[];
    onGptSelected: (gpt: IGptConfiguration, isPinned: boolean) => void;
    firstCardTitle?: string;
    firstCardDescription?: string;
    firstCardIcon?: ReactElement;
    firstCardAction?: () => void;
    lastCardDescription?: string;
    lastCardAction?: () => void;
    lastCardIcon?: ReactElement;
}

// displays the list of GPTs as cards in the SyGPT tools section
export const ExploreGptSection: FC<ExploreGptSectionProps> = ({ title, infoLabel, gpts, onGptSelected, firstCardTitle, firstCardDescription, firstCardAction, firstCardIcon, lastCardDescription, lastCardAction, lastCardIcon }: ExploreGptSectionProps) => {
    const styles = useStyles();
    const { dispatch } = useRootState();
    const { userPreferences } = useUserPreferences();
    
    const isPinned = (gptId: string) => userPreferences.addedGpts?.includes(gptId) ?? false;
    const [ currentGpts, setCurrentGpts] = useState<IGptConfiguration[]>(gpts);
    const pollingId = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        setCurrentGpts(gpts);
        // gpts here can only be personal type
        // if there are GPTs with status running and last update is within 1 day, start polling the plugin GET API
        if (gpts.some(x => x.status === GptStatus.Running && (moment(x.lastUpdateOn).isAfter(moment().subtract(1, "day"))))){
            pollingId.current = setInterval(() =>{
                performEnterpriseGPTApiCall(async (client) => {
                    try {
                        // get the info about all the personal GPTs in a single call
                        const gptConfigurations = await client.getPersonalGpts();
                        setCurrentGpts(gptConfigurations);
                        dispatch(editGptConfigurations(gptConfigurations));
                        // if there are no personal GPTs running, stop polling
                        if (!gpts.some(x => x.isPersonal && x.status === GptStatus.Running)){
                            clearInterval(pollingId.current!);
                        }
                    }
                    catch {
                        // if there is an error, stop polling
                        clearInterval(pollingId.current!);
                    }
                });
            }, 10000);
        }
        // cleanup
        return () => clearInterval(pollingId.current);
    }, [gpts]);

    return <div>
        <Subtitle1>{title}</Subtitle1>
        {infoLabel && <InfoLabel info={infoLabel} />}
        <div className={styles.gptsContainer}>
            {
                firstCardTitle && <Card key='first_card' className={styles.card} onClick={firstCardAction}>
                    <div className={styles.staticCardContainer}>
                        <Subtitle1>{firstCardTitle}</Subtitle1>
                        {firstCardIcon}
                        <Body2 align="center">{firstCardDescription}</Body2>
                    </div>
                </Card>
            }
            {
                currentGpts.map((gpt) => {             
                    return <Card
                        key={gpt.id}
                        className={styles.card}
                        onClick={() => onGptSelected(gpt, isPinned(gpt.id!))}> 
                        <CardHeader
                            image={
                                <Image
                                    className={styles.headerImage}
                                    src={getGptIconAsset(gpt.icon)}
                                />
                            }
                            header={<Subtitle2>{gpt.name}</Subtitle2>}
                            description={
                                <Caption1>{gpt.isPersonal ? 'Created by you' : 'By Syensqo.ai'}</Caption1>
                            }
                        />
                        <div className={styles.cardBody}>
                            <p>{gpt.description}</p>
                        </div>
                        <CardFooter>
                            { // show the Added value only if it is SyGPT (when available on the list), if it is a pinned, featured tool or if it is a pinned, CYOT tool in status Ready
                            (gpt.isDefault || (isPinned(gpt.id!) && ((!gpt.isPersonal || gpt.status === GptStatus.Ready)))) && <Badge>Added</Badge>}
                            { // show the Ready value only if it is a CYOT tool in status Ready and not pinned
                            gpt.isPersonal && gpt.status === GptStatus.Ready && !isPinned(gpt.id!) && <Badge color="success">Ready</Badge>}
                            { // show the Running value only if it is a CYOT tool in status Running (currently, the plugin return Running status even if there was an error)
                            gpt.isPersonal && gpt.status === GptStatus.Running && <Badge color="warning">Running</Badge>}
                        </CardFooter>
                    </Card>;
                })
            }
            {
                // special card to be used at the end of the list
                lastCardDescription && <Card key='last_card' className={styles.card} onClick={lastCardAction}>
                    <div className={styles.staticCardContainer}>
                        <Body2 align="center">{lastCardDescription}</Body2>
                        {lastCardIcon}
                    </div>
                </Card>
            }
        </div>
    </div>;
}